import React, { forwardRef, memo } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { AccessibilityHelper } from '@/classes';

/**
 * ClickableElement
 * @param {import('.').ClickableElementProps} props
 * @param {React.ForwardedRef<HTMLElement>} ref
 */
function ClickableElement({ children, className, onClick, tagName, ...aria }, ref) {
  const TagType = `${tagName}`;
  return (
    <TagType
      role="button"
      tabIndex="0"
      onKeyDown={(/** @type {React.KeyboardEvent} */ event) => {
        AccessibilityHelper.handleKeydownEvent(event, onClick);
      }}
      className={className}
      onClick={onClick}
      ref={ref}
      {...aria}
    >
      {children}
    </TagType>
  );
}

// eslint-disable-next-line no-func-assign
ClickableElement = forwardRef(ClickableElement);

ClickableElement.propTypes = {
  /** All child elements wrapped within the ClickableElement */
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  /** Emotion styles */
  className: PropTypes.string.isRequired,
  /** What to do when clicked */
  onClick: PropTypes.func.isRequired,
  /** Type of HTML element (div, span, header, etc) defaults to div */
  tagName: PropTypes.string,
};

ClickableElement.defaultProps = {
  tagName: 'div',
  children: null,
};

const styledClickableElement = memo(styled(ClickableElement)`
  cursor: pointer;
`);

export default styledClickableElement;
